/* eslint-disable */
// @ts-nocheck
// Force UI Deploy

import AppProvider from "@providers/AppProvider";
import AppErrorBoundary from "@components/AppErrorBoundary";
import config from "env.config";
import { initDatadogContext, logger } from "@/utils/loggingUtils";

import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { isCspViolationReport } from "@shared/csp";

/**
 * Non-breaking error patterns to suppress from Datadog error tracking.
 * These are known noise that do not impact user flow:
 *  - Learnosity internal errors (DOM element missing, Empty reason, tokenHighlight)
 *  - Learnosity Player init errors and timeouts (UI shows error page, user retries)
 *  - Learnosity passive touch event intervention warnings
 *  - Plotly resize race conditions (chart still renders)
 *  - login_refresh failures (handled by auth redirect)
 */
export const SUPPRESSED_ERROR_PATTERNS = [
  "DOM element missing",
  "no matching DOM element",
  "The object can not be found here",
  "Empty reason",
  "Script error.",
  "tokenHighlight",
  "Resize must be passed a displayed plot div element",
  "login_refresh",
  "LearnosityItems.errors",
  "LearnosityApp.errors",
  "LearnosityAssess.errors",
  "[Learnosity Player]",
  "Learnosity init error",
  "init timed out",
  "Ignored attempt to cancel",
  "Save responses failed",
  "Failed loading Questions API",
  "Unable to render latex",
  "The node to be removed is not a child of this node",
  "bat.bing.com",
  "google-analytics.com",
  "googletagmanager.com",
  "doubleclick.net",
  "Axios Error (Response): 401",
  "wasm streaming compile failed",
  "falling back to ArrayBuffer",
  "dotlottie-player.wasm",
  "prism-autoloader.min.js",
  "cdn.jsdelivr.net",
  "ResizeObserver loop",
  // iOS WKWebView / browser extension / third-party noise
  "did not respond to this postMessage",
  '{"isTrusted":true}',
  "reading 'M_ID'",
  "MetaMask"
];

export const isNonBreakingError = (message: string | undefined): boolean => {
  if (!message) return false;
  return SUPPRESSED_ERROR_PATTERNS.some(pattern => message.includes(pattern));
};

export const isNetworkConnectivityError = (
  message: string | undefined
): boolean => {
  if (!message) return false;
  return (
    message.includes("ERR_NETWORK") ||
    message.includes("Possible local connectivity issue") ||
    message.includes("Network Error") ||
    message.includes("Failed to load") ||
    message.includes("Failed to fetch") ||
    message.includes("Load failed") ||
    message.includes("XHR error") ||
    message.includes("Fetch error")
  );
};

if (
  config &&
  config.DATADOG_APPLICATION_ID !== undefined &&
  config.DATADOG_APPLICATION_ID !== null
) {
  const serviceName = `oex-learning-ui-${config.PIPELINE_ENVIRONMENT}`;
  const toOrigin = (value: string): string | null => {
    if (!value) {
      return null;
    }

    try {
      return new URL(value).origin;
    } catch {
      return null;
    }
  };

  const allowedTracingUrls = [toOrigin(config.BASE_URL)].filter(Boolean);

  try {
    datadogRum.init({
      applicationId: config.DATADOG_APPLICATION_ID,
      clientToken: config.DATADOG_CLIENT_TOKEN,
      site: "datadoghq.com",
      service: serviceName,
      env: config.PIPELINE_ENVIRONMENT,
      version: config.UI_VERSION,
      sessionSampleRate: parseInt(config.DATADOG_SESSION_SAMPLE_RATE) || 100,
      sessionReplaySampleRate:
        parseInt(config.DATADOG_SESSION_REPLAY_SAMPLE_RATE) || 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
      allowedTracingUrls,
      beforeSend: event => {
        // Suppress non-breaking RUM error events
        if (event.type === "error") {
          const msg = event.error?.message ?? "";
          const stack = event.error?.stack ?? "";
          // Suppress CSP violations from RUM Error Tracking — they are
          // report-only and handled as warn-level in the Logs beforeSend.
          if (isCspViolationReport(msg)) return false;
          if (isNonBreakingError(msg)) return false;
          if (isNetworkConnectivityError(msg)) return false;

          // Suppress JSON parse errors from dotlottie WASM fetch failures --
          // the WASM fetch fails (e.g. blocked CDN) and dotlottie tries to
          // parse the empty response as JSON, throwing "Unexpected EOF".
          if (msg.includes("JSON Parse error") && stack.includes("dotlottie")) {
            return false;
          }
        }
        return true;
      }
    });

    datadogLogs.init({
      clientToken: config.DATADOG_CLIENT_TOKEN,
      site: "datadoghq.com",
      service: serviceName,
      env: config.PIPELINE_ENVIRONMENT,
      version: config.UI_VERSION,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ["warn", "error"],
      forwardReports: "all",
      sessionSampleRate: parseInt(config.DATADOG_SESSION_SAMPLE_RATE) || 100,
      beforeSend: log => {
        const msg = log.message ?? "";

        // CSP violations are report-only — downgrade from error to warn so they
        // don't trigger error-level alerts but remain visible for review.
        // Tag with ddsource so they're filterable as @ddsource:csp-report.
        if (isCspViolationReport(msg)) {
          log.status = "warn";
          log.ddsource = "csp-report";
          return true;
        }

        // Suppress non-breaking errors entirely
        if (log.status === "error" && isNonBreakingError(msg)) return false;

        // Downgrade network connectivity errors from error to warn
        if (log.status === "error" && isNetworkConnectivityError(msg)) {
          log.status = "warn";
        }

        // Suppress noisy section fetch cancellation warnings (~thousands/hr)
        if (msg.includes("[Fetch Section Effect]")) return false;

        // Suppress noisy Lesson Thunk fetch warnings
        if (msg.includes("[Lesson Thunk] Error fetching section content"))
          return false;

        return true;
      }
    });

    initDatadogContext();

    logger.info("Initialised Datadog logger successfully", {
      action: "datadog_init",
      system: "OEX",
      service: serviceName,
      env: config.PIPELINE_ENVIRONMENT,
      version: config.UI_VERSION
    });
  } catch (exception) {
    console.log("DD Failed!", { error: exception });
  }
}

export function App() {
  return (
    <AppErrorBoundary>
      <AppProvider />
    </AppErrorBoundary>
  );
}

export default App;
